import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Users, CheckCircle, MessageSquare, Ticket } from 'lucide-react';

const Contact = () => {
  const contactInfo = [
    {
      icon: MessageSquare,
      title: 'تیکت پشتیبانی آنلاین',
      value: 'ثبت و پیگیری تیکت‌ها',
      description: 'سریع‌ترین راه ارتباطی مستقیم با تیم پشتیبانی فنی و مدیریت سرور',
      isTicket: true,
      link: '/tickets'
    },
    {
      icon: Users,
      title: 'کانال روبیکا',
      value: 'کانال رسمی سرور',
      description: 'اطلاع‌رسانی آخرین اخبار، مسابقات و ارتباط با استف‌های سرور',
      links: [
        { name: 'کانال روبیکا', url: 'https://rubika.ir/AvalonMc' },
        { name: 'لیست استف‌ها در روبیکا', url: 'https://rubika.ir/AvStaffList' }
      ]
    },
    {
      icon: Mail,
      title: 'پشتیبانی ایمیل',
      value: 'AvalonMcPe@gmail.com',
      link: 'mailto:AvalonMcPe@gmail.com',
      description: 'پاسخگویی به سوالات و استعلام‌ها از طریق ایمیل رسمی'
    }
  ];

  const faqs = [
    {
      q: "چگونه می‌توانم تیکت پشتیبانی ثبت کنم؟",
      a: "شما می‌توانید با ورود به حساب کاربری خود و مراجعه به بخش تیکت‌ها، درخواست یا مشکل خود را ثبت کنید. پاسخ ادمین‌ها در کوتاه‌ترین زمان ممکن برای شما ارسال می‌شود."
    },
    {
      q: "چگونه می‌توانم رنک خریداری کنم؟",
      a: "برای خرید رنک، ابتدا وارد حساب کاربری خود شوید، سپس به صفحه خرید رنک مراجعه کرده و رنک مورد نظر را انتخاب و مشخصات پرداخت را ثبت کنید."
    },
    {
      q: "چگونه می‌توانم با استف‌های سرور در ارتباط باشم؟",
      a: "با مراجعه به کانال روبیکا استف‌لیست اوالون می‌توانید آیدی تمامی ادمین‌ها و پشتیبانان سرور را مشاهده و با آنها در ارتباط باشید."
    },
    {
      q: "زمان پاسخگویی به تیکت‌ها چقدر است؟",
      a: "تیم پشتیبانی ما در کمتر از چند ساعت (حداکثر ۱۲ ساعت) به تمام تیکت‌های شما پاسخ می‌دهد."
    },
    {
      q: "آیا امکان بازگشت وجه وجود دارد؟",
      a: "در صورتی که رنک خریده شده هنوز تحویل داده نشده باشد می‌توانید درخواست عودت دهید، پس از تحویل رنک امکان بازگشت وجه وجود ندارد."
    }
  ];

  return (
    <div className="min-h-screen bg-[#050811] text-white pt-20 pb-20" dir="rtl">
      
      {/* Header Banner */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden bg-gradient-to-b from-emerald-900/30 via-cyan-900/20 to-transparent py-20 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-3xl mb-6 shadow-xl shadow-emerald-500/20">
            <MessageSquare className="w-12 h-12 text-white" />
          </div>

          <h1 className="text-4xl md:text-6xl font-black text-white mb-4">
            ارتباط با ما
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            تیم پشتیبانی اوالون ام‌سی همیشه آماده پاسخگویی به سوالات و مشکلات شماست
          </p>
        </div>
      </motion.header>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="space-y-20">

          {/* Contact Cards */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-10 flex items-center gap-3">
              <Ticket className="w-7 h-7 text-emerald-400" />
              <span>راه‌های ارتباطی</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactInfo.map((info, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="bg-white/5 border border-white/10 rounded-3xl p-8 flex flex-col justify-between hover:border-emerald-500/50 transition-all hover:-translate-y-1"
                >
                  <div>
                    <div className="w-16 h-16 mb-6 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <info.icon className="w-8 h-8 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-white mb-3">
                      {info.title}
                    </h3>

                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                      {info.description}
                    </p>
                  </div>

                  <div>
                    {info.isTicket ? (
                      <Link
                        to="/tickets"
                        className="block text-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-2xl shadow-md transition-all text-sm"
                      >
                        ثبت و پیگیری تیکت‌ها ←
                      </Link>
                    ) : info.links ? (
                      <div className="space-y-3">
                        {info.links.map((link, j) => (
                          <a
                            key={j}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-center bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold text-sm border border-white/10 transition-all"
                          >
                            {link.name}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <a
                        href={info.link}
                        className="block text-center bg-white/10 hover:bg-white/20 text-emerald-400 font-bold py-3 rounded-xl text-sm border border-white/10 transition-all dir-ltr"
                      >
                        {info.value}
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
              <span>سوالات متداول</span>
            </h3>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all"
                >
                  <h4 className="flex items-center gap-3 text-lg font-bold text-white mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed pr-5">
                    {faq.a}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Contact;